import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DropzoneForm from "../../../../components/DropzoneForm/DropzoneForm";
import Messages from "../../../../localization/Messages";
import { showBackendMessage } from "../../../../helpers/messagesHelper";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { IPortDocument } from "../../../../models/portDocument";
import { IDocument } from "../../../../models/document";
import {
    deletePortDocumentDisp,
    getPortDocumentsDisp,
    savePortDocumentDisp,
} from "../../../../api/portDocuments";
import {
    getUrlForDownload,
    getUrlForUpload,
    uploadFileToStorage,
} from "../../../../api/document";

export interface IPortDocumentsEditor {
    destinationId: string;
}

interface EditState {
    open: boolean;
    portDocument: IPortDocument;
    titleError?: string;
    fileOrUrlError?: string;
}

const emptyPortDocument = (): IPortDocument => ({
    id: "",
    title: "",
    document: null,
    url: "",
});

/**
 * Dispatcher editor for the Port Information section of a destination.
 * Lists existing entries and provides add/edit/delete operations.
 */
export const PortDocumentsEditor = ({ destinationId }: IPortDocumentsEditor) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const withConfirm = useConfirmDialog();
    const [items, setItems] = useState<IPortDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState<EditState>({
        open: false,
        portDocument: emptyPortDocument(),
    });

    const fetchItems = useCallback(async () => {
        if (!destinationId) return;
        try {
            setLoading(true);
            const res = await getPortDocumentsDisp(destinationId);
            setItems(res.data ?? []);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.portDocuments));
        } finally {
            setLoading(false);
        }
    }, [destinationId, dispatch, intl]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const openAdd = () =>
        setEdit({ open: true, portDocument: emptyPortDocument() });
    const openEdit = (pd: IPortDocument) =>
        setEdit({ open: true, portDocument: { ...pd } });
    const closeEdit = () =>
        setEdit({ open: false, portDocument: emptyPortDocument() });

    const updateEdit = (diff: Partial<IPortDocument>) =>
        setEdit(prev => ({
            ...prev,
            portDocument: { ...prev.portDocument, ...diff },
            titleError: diff.title !== undefined ? undefined : prev.titleError,
            fileOrUrlError:
                diff.document !== undefined || diff.url !== undefined
                    ? undefined
                    : prev.fileOrUrlError,
        }));

    const handleUploadFile = async (file: File) => {
        try {
            const urlRes = await getUrlForUpload(file);
            await uploadFileToStorage(file, urlRes.data.uploadUrl);
            const document: IDocument = {
                mimeType: file.type,
                lastModify: file.lastModified,
                name: file.name,
                size: file.size,
                path: urlRes.data.path,
            };
            updateEdit({ document });
        } catch {
            dispatch(showBackendMessage(intl, "error", "adding", Messages.document));
        }
    };

    const handleSave = async () => {
        const pd = edit.portDocument;
        const titleError = pd.title.trim()
            ? undefined
            : intl.formatMessage(Messages.portDocumentTitleIsRequired);
        const hasFile = Boolean(pd.document?.path);
        const hasUrl = Boolean(pd.url && pd.url.trim());
        const fileOrUrlError =
            hasFile || hasUrl
                ? undefined
                : intl.formatMessage(Messages.portDocumentFileOrUrlRequired);
        if (titleError || fileOrUrlError) {
            setEdit(prev => ({ ...prev, titleError, fileOrUrlError }));
            return;
        }
        try {
            setLoading(true);
            await savePortDocumentDisp(destinationId, {
                ...pd,
                title: pd.title.trim(),
                url: hasUrl ? pd.url!.trim() : null,
                document: hasFile ? pd.document! : null,
            });
            dispatch(showBackendMessage(intl, "success", "updating", Messages.portDocuments));
            closeEdit();
            await fetchItems();
        } catch {
            dispatch(showBackendMessage(intl, "error", "updating", Messages.portDocuments));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (pd: IPortDocument) => {
        withConfirm({
            message: intl.formatMessage(Messages.confirmPortDocumentDeleteText),
            onConfirm: async () => {
                try {
                    setLoading(true);
                    await deletePortDocumentDisp(destinationId, pd.id);
                    dispatch(
                        showBackendMessage(intl, "success", "deleting", Messages.portDocuments),
                    );
                    await fetchItems();
                } catch {
                    dispatch(
                        showBackendMessage(intl, "error", "deleting", Messages.portDocuments),
                    );
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleDownload = async (doc: IDocument) => {
        try {
            const res = await getUrlForDownload(doc.name, doc.path, true);
            window.open(res.data, "_blank");
        } catch {
            dispatch(showBackendMessage(intl, "error", "downloading", Messages.document));
        }
    };

    return (
        <Box sx={{ mt: 2, px: 2, mb: 4 }}>
            <Box display="flex" alignItems="center" gap="1.5rem" mb={1}>
                <h3 style={{ margin: 0, fontSize: "1.17em", fontWeight: 400 }}>
                    {intl.formatMessage(Messages.portInformation)}
                </h3>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAdd}
                    disabled={!destinationId || loading}
                >
                    {intl.formatMessage(Messages.addPortDocument)}
                </Button>
            </Box>
            <TableContainer sx={{ width: "fit-content" }}>
                <Table size="small" sx={{ width: "fit-content" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{intl.formatMessage(Messages.portDocumentTitle)}</TableCell>
                            <TableCell>{intl.formatMessage(Messages.document)}</TableCell>
                            <TableCell>{intl.formatMessage(Messages.weblink)}</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {intl.formatMessage(Messages.noPortDocuments)}
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map(pd => (
                            <TableRow key={pd.id}>
                                <TableCell>{pd.title}</TableCell>
                                <TableCell>
                                    {pd.document ? (
                                        <Tooltip
                                            title={intl.formatMessage(Messages.downloadFile)}
                                        >
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() => handleDownload(pd.document!)}
                                            >
                                                {pd.document.name}
                                            </Link>
                                        </Tooltip>
                                    ) : (
                                        "-"
                                    )}
                                </TableCell>
                                <TableCell>
                                    {pd.url ? (
                                        <Link
                                            href={pd.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {pd.url}
                                        </Link>
                                    ) : (
                                        "-"
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openEdit(pd)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(pd)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={edit.open} onClose={closeEdit} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {edit.portDocument.id
                        ? intl.formatMessage(Messages.editPortDocument)
                        : intl.formatMessage(Messages.addPortDocument)}
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField
                            required
                            label={intl.formatMessage(Messages.portDocumentTitle)}
                            value={edit.portDocument.title}
                            onChange={e => updateEdit({ title: e.target.value })}
                            error={Boolean(edit.titleError)}
                            helperText={edit.titleError}
                            variant="filled"
                            fullWidth
                        />
                        <TextField
                            label={intl.formatMessage(Messages.weblink)}
                            value={edit.portDocument.url ?? ""}
                            onChange={e => updateEdit({ url: e.target.value })}
                            placeholder="https://..."
                            variant="filled"
                            fullWidth
                        />
                        <Box>
                            <p style={{ marginBottom: 4 }}>
                                {intl.formatMessage(Messages.document)}
                            </p>
                            {edit.portDocument.document && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <span>{edit.portDocument.document.name}</span>
                                    <Button
                                        size="small"
                                        onClick={() => updateEdit({ document: null })}
                                    >
                                        {intl.formatMessage(Messages.delete)}
                                    </Button>
                                </Box>
                            )}
                            <DropzoneForm onUpload={handleUploadFile} />
                        </Box>
                        {edit.fileOrUrlError && (
                            <Box color="error.main">{edit.fileOrUrlError}</Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit}>{intl.formatMessage(Messages.cancel)}</Button>
                    <Button variant="contained" onClick={handleSave} disabled={loading}>
                        {intl.formatMessage(Messages.save)}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PortDocumentsEditor;
